import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

export const CurrentReadsPage = () => {
  const [manuscripts, setManuscripts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchManuscripts();
  }, []);

  const fetchManuscripts = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.betaApplications.myActiveReads, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to load manuscripts');
        setLoading(false);
        return;
      }

      // Transform backend data to frontend format
      const transformedManuscripts = data.assignments.map((assignment: any) => {
        const assignedDate = new Date(assignment.assignedDate);
        const formattedAssignedDate = assignedDate.toISOString().split('T')[0];

        let formattedDeadline = null;
        if (assignment.deadline) {
          const deadline = new Date(assignment.deadline);
          formattedDeadline = deadline.toISOString().split('T')[0];
        }

        return {
          id: assignment.manuscriptId,
          assignmentId: assignment.id,
          title: assignment.title,
          author: assignment.author,
          genre: formatGenre(assignment.genre),
          wordCount: assignment.wordCount,
          progress: assignment.progress || 0,
          deadline: formattedDeadline,
          daysRemaining: assignment.daysRemaining || 0,
          status: assignment.status,
          assignedDate: formattedAssignedDate,
          downloadUrl: `/downloads/${assignment.manuscriptId}.pdf`,
          notes: 0, // Not yet implemented
          bookmarks: 0, // Not yet implemented
          feedbackSubmitted: assignment.feedbackSubmitted,
        };
      });

      setManuscripts(transformedManuscripts);
      setLoading(false);
    } catch (err) {
      console.error('Fetch manuscripts error:', err);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  // Helper to format backend genre enum to frontend
  const formatGenre = (genre: string) => {
    const genreMap: Record<string, string> = {
      FANTASY: 'Fantasy',
      SCIENCE_FICTION: 'Science Fiction',
      MYSTERY: 'Mystery',
      THRILLER: 'Thriller',
      ROMANCE: 'Romance',
      LITERARY_FICTION: 'Literary Fiction',
      HISTORICAL_FICTION: 'Historical Fiction',
      HORROR: 'Horror',
      YOUNG_ADULT: 'Young Adult',
      MIDDLE_GRADE: 'Middle Grade',
      CONTEMPORARY: 'Contemporary',
      PARANORMAL: 'Paranormal',
      DYSTOPIAN: 'Dystopian',
      OTHER: 'Other',
    };
    return genreMap[genre] || genre;
  };

  // Unused mock data - keeping for reference
  const _unused_mockManuscripts = [
    {
      id: 1,
      title: 'Echoes of the Archive',
      author: 'Eleanor Hart',
      genre: 'Historical Fiction',
      wordCount: 95000,
      progress: 65,
      deadline: '2024-02-15',
      daysRemaining: 8,
      status: 'reading',
      assignedDate: '2024-01-20',
      downloadUrl: '/downloads/echoes-of-the-archive.pdf',
      notes: 3,
      bookmarks: 5,
    },
    {
      id: 2,
      title: 'The Cartographer\'s Secret',
      author: 'Michael Torres',
      genre: 'Mystery',
      wordCount: 82000,
      progress: 30,
      deadline: '2024-02-20',
      daysRemaining: 13,
      status: 'reading',
      assignedDate: '2024-01-28',
      downloadUrl: '/downloads/cartographers-secret.pdf',
      notes: 1,
      bookmarks: 2,
    },
    {
      id: 3,
      title: 'The Last Garden',
      author: 'Maria Santos',
      genre: 'Fantasy',
      wordCount: 95000,
      progress: 0,
      deadline: '2024-02-25',
      daysRemaining: 18,
      status: 'not_started',
      assignedDate: '2024-02-01',
      downloadUrl: '/downloads/last-garden.pdf',
      notes: 0,
      bookmarks: 0,
    },
  ];

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return theme.colors.success;
    if (progress >= 50) return theme.colors.primary;
    if (progress >= 25) return theme.colors.warning;
    return theme.colors.textTertiary;
  };

  const getStatusBadge = (status: string, progress: number) => {
    if (status === 'not_started' || progress === 0) {
      return <Badge variant="default">Not Started</Badge>;
    } else if (progress >= 100) {
      return <Badge variant="success">Completed</Badge>;
    } else {
      return <Badge variant="primary">In Progress</Badge>;
    }
  };

  const handleAbandon = (manuscriptId: number, title: string) => {
    if (window.confirm(`Are you sure you want to abandon "${title}"? The author will be notified.`)) {
      console.log('Abandoning manuscript:', manuscriptId);
      window.alert('You have withdrawn from this beta reading assignment. The author has been notified.');
      // In real app, would update via API and refresh
    }
  };

  const handleDownload = (url: string, title: string) => {
    console.log('Downloading manuscript:', title);
    window.alert(`Downloading "${title}"...\n\nIn a real application, this would download the manuscript file.`);
    // In real app, would trigger actual download
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Current Reads
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Manuscripts you're currently beta reading
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Loading manuscripts...
              </p>
            </div>
          </Card>
        )}

        {/* Error State */}
        {!loading && error && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.error,
                marginBottom: '20px',
              }}>
                {error}
              </p>
              <Button onClick={fetchManuscripts}>
                Try Again
              </Button>
            </div>
          </Card>
        )}

        {/* Stats Card */}
        {!loading && !error && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '32px',
        }}>
          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '500',
              color: theme.colors.primary,
              marginBottom: '4px',
            }}>
              {manuscripts.length}
            </div>
            <div style={{
              fontSize: '13px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Active Manuscripts
            </div>
          </Card>

          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '500',
              color: theme.colors.warning,
              marginBottom: '4px',
            }}>
              {manuscripts.filter(m => m.daysRemaining <= 7).length}
            </div>
            <div style={{
              fontSize: '13px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Due This Week
            </div>
          </Card>

          <Card style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '500',
              color: theme.colors.success,
              marginBottom: '4px',
            }}>
              {Math.round(manuscripts.reduce((acc, m) => acc + m.progress, 0) / manuscripts.length)}%
            </div>
            <div style={{
              fontSize: '13px',
              color: theme.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Average Progress
            </div>
          </Card>
        </div>
        )}

        {/* Manuscripts List */}
        {!loading && !error && manuscripts.length === 0 ? (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
                marginBottom: '16px',
              }}>
                You're not currently reading any manuscripts
              </p>
              <Button onClick={() => window.location.href = '/manuscripts/discover'}>
                Discover Manuscripts
              </Button>
            </div>
          </Card>
        ) : !loading && !error ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}>
            {manuscripts.map((manuscript) => (
              <Card key={manuscript.id}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '24px',
                }}>
                  <div style={{ flex: 1 }}>
                    {/* Title and Status */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '8px',
                      flexWrap: 'wrap',
                    }}>
                      <h3 style={{
                        fontSize: '22px',
                        fontWeight: '500',
                        color: theme.colors.textPrimary,
                        margin: 0,
                      }}>
                        {manuscript.title}
                      </h3>
                      {getStatusBadge(manuscript.status, manuscript.progress)}
                    </div>

                    {/* Author */}
                    <p style={{
                      fontSize: '16px',
                      color: theme.colors.textSecondary,
                      marginBottom: '12px',
                    }}>
                      by {manuscript.author}
                    </p>

                    {/* Metadata */}
                    <div style={{
                      display: 'flex',
                      gap: '16px',
                      fontSize: '14px',
                      color: theme.colors.textSecondary,
                      marginBottom: '16px',
                      flexWrap: 'wrap',
                    }}>
                      <span>{manuscript.genre}</span>
                      <span>•</span>
                      <span>{manuscript.wordCount.toLocaleString()} words</span>
                      <span>•</span>
                      <span>Assigned: {manuscript.assignedDate}</span>
                    </div>

                    {/* Deadline Warning */}
                    <div style={{
                      padding: '12px',
                      backgroundColor: manuscript.daysRemaining <= 5
                        ? theme.colors.errorLight
                        : manuscript.daysRemaining <= 10
                          ? theme.colors.warningLight
                          : theme.colors.backgroundAlt,
                      borderRadius: theme.borderRadius.small,
                      marginBottom: '16px',
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '14px',
                      }}>
                        <span style={{
                          color: theme.colors.textPrimary,
                          fontWeight: '500',
                        }}>
                          Due: {manuscript.deadline}
                        </span>
                        <span style={{
                          color: manuscript.daysRemaining <= 5
                            ? theme.colors.error
                            : manuscript.daysRemaining <= 10
                              ? theme.colors.warning
                              : theme.colors.textSecondary,
                          fontWeight: '500',
                        }}>
                          {manuscript.daysRemaining} days remaining
                        </span>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        fontSize: '14px',
                        color: theme.colors.textSecondary,
                      }}>
                        <span>Reading Progress</span>
                        <span>{manuscript.progress}%</span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '10px',
                        backgroundColor: theme.colors.border,
                        borderRadius: theme.borderRadius.small,
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${manuscript.progress}%`,
                          height: '100%',
                          backgroundColor: getProgressColor(manuscript.progress),
                          transition: 'width 0.3s ease',
                        }}></div>
                      </div>
                    </div>

                    {/* Notes and Bookmarks */}
                    {(manuscript.notes > 0 || manuscript.bookmarks > 0) && (
                      <div style={{
                        display: 'flex',
                        gap: '16px',
                        fontSize: '14px',
                        color: theme.colors.textSecondary,
                        marginBottom: '16px',
                      }}>
                        {manuscript.notes > 0 && (
                          <span>📝 {manuscript.notes} note{manuscript.notes !== 1 ? 's' : ''}</span>
                        )}
                        {manuscript.bookmarks > 0 && (
                          <span>🔖 {manuscript.bookmarks} bookmark{manuscript.bookmarks !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      flexWrap: 'wrap',
                    }}>
                      <Button
                        size="small"
                        onClick={() => window.location.href = `/reader/manuscripts/${manuscript.id}/read`}
                      >
                        {manuscript.progress === 0 ? 'Start Reading' : 'Continue Reading'}
                      </Button>

                      <Button
                        size="small"
                        variant="outline"
                        onClick={() => handleDownload(manuscript.downloadUrl, manuscript.title)}
                      >
                        Download
                      </Button>

                      {manuscript.progress >= 50 && (
                        <Button
                          size="small"
                          variant="outline"
                          onClick={() => window.location.href = `/reader/manuscripts/${manuscript.id}/feedback`}
                        >
                          Provide Feedback
                        </Button>
                      )}

                      <Button
                        size="small"
                        variant="ghost"
                        onClick={() => handleAbandon(manuscript.id, manuscript.title)}
                      >
                        Abandon
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : null}

        {/* Help Card */}
        {!loading && !error && (
          <Card style={{
            backgroundColor: theme.colors.backgroundAlt,
            marginTop: '40px',
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '500',
              marginBottom: '12px',
              color: theme.colors.textPrimary,
            }}>
              Beta Reading Tips
            </h3>
            <ul style={{
              margin: 0,
              paddingLeft: '20px',
              fontSize: '15px',
              lineHeight: '1.8',
              color: theme.colors.textSecondary,
            }}>
              <li>Set aside dedicated time for reading to stay on track with deadlines</li>
              <li>Take notes as you read - don't rely on memory for feedback</li>
              <li>Use the bookmark feature to mark important passages or issues</li>
              <li>If you're falling behind, communicate with the author early</li>
              <li>You can provide interim feedback even before finishing the entire manuscript</li>
              <li>Only abandon a commitment if absolutely necessary - authors are counting on you</li>
            </ul>
          </Card>
        )}
      </main>

      <Footer />
    </div>
  );
};
